import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const PAYMENT_DATA_KEY = 'payment_data'

// Reading the `booking-flow` zustand store's own persisted sessionStorage
// entry directly (rather than importing the store) — this file runs before
// any component/store hook exists, at module load time.
const BOOKING_FLOW_STORAGE_KEY = 'booking-flow'

function isMidPayment(): boolean {
  try {
    const raw = sessionStorage.getItem(BOOKING_FLOW_STORAGE_KEY)
    if (!raw) return false
    const step = JSON.parse(raw)?.state?.currentPaymentStep
    return step === 2 || step === 3
  } catch {
    return false
  }
}

// sessionStorage survives a hard reload (F5 / reload button) by design, but
// a reload here means the user has left and come back to a fresh page
// load — any payment_data from before must not resurface. Client-side
// navigation between booking steps never re-executes this module, so this
// runs exactly once per real page load and only strips the key when the
// Navigation Timing API reports that load as an actual reload (not the
// initial load, a back/forward navigation, or a prerender).
//
// Exception: step 2/3 means a booking already exists and the payment UI
// depends on payment_data (bookingNo, cardItems, totalAmount, ...) to render
// at all — wiping it there would blank out an in-progress payment the user
// is actively completing. Only step 1 (no booking created yet) is safe to
// treat as "start clean".
if (typeof window !== 'undefined') {
  try {
    const [navEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    if (navEntry?.type === 'reload' && !isMidPayment()) {
      sessionStorage.removeItem(PAYMENT_DATA_KEY)
    }
  } catch (error) {
    // Navigation Timing API unavailable in this environment — nothing safe
    // to do about it, leave existing data untouched.
    console.error('Error checking navigation type for payment data cleanup:', error)
  }
}

interface PaymentData {
  bookingNo?: string
  customerName?: string
  phone?: string
  email?: string
  gender?: string
  timeIn?: string
  timeOut?: string
  nights?: number
  totalRooms?: number
  totalAmount?: number
  totalChild?: number
  totalAdult?: number
  totalTime?: number
  totalStay?: number
  arrivalDate?: string
  departureDate?: string
  cancelTime: number
  cardItems?: any[]
  voucherCode?: string
  // Which auth token (memberRef) this identity/voucher data belongs to —
  // null/absent means it was captured while not logged in. Used to detect,
  // even across a full page reload, that cached data belongs to a different
  // account than the one now active and must be sanitized before reuse.
  authOwner?: string | null
  [key: string]: any // Allow for other payment data fields
}

const paymentDataStorage = {
  get: async (): Promise<PaymentData | null> => {
    try {
      const data = sessionStorage.getItem(PAYMENT_DATA_KEY)
      return data ? JSON.parse(data) : null
    } catch (error) {
      console.error('Error reading payment data:', error)
      return null
    }
  },
  set: async (data: PaymentData | null): Promise<void> => {
    try {
      if (data) {
        sessionStorage.setItem(PAYMENT_DATA_KEY, JSON.stringify(data))
      } else {
        sessionStorage.removeItem(PAYMENT_DATA_KEY)
      }
    } catch (error) {
      console.error('Error saving payment data:', error)
    }
  },
  clearSpecificFields: async (): Promise<PaymentData | null> => {
    try {
      const data = sessionStorage.getItem(PAYMENT_DATA_KEY)
      if (!data) return null

      const currentData = JSON.parse(data) as PaymentData
      const updatedData = {
        ...currentData,
        cardItems: [],
        totalAmount: 0,
        totalRooms: 0,
        bookingNo: '',
        bookingId: '',
      }

      sessionStorage.setItem(PAYMENT_DATA_KEY, JSON.stringify(updatedData))
      return updatedData
    } catch (error) {
      console.error('Error clearing specific payment data fields:', error)
      return null
    }
  },
}

export function usePaymentData() {
  const queryClient = useQueryClient()

  const { data } = useQuery<PaymentData | null>({
    queryKey: [PAYMENT_DATA_KEY],
    queryFn: paymentDataStorage.get,
    staleTime: Infinity,
  })

  const setPaymentDataMutation = useMutation<PaymentData | null, Error, PaymentData | null>({
    mutationFn: async (newData: PaymentData | null) => {
      await paymentDataStorage.set(newData)
      return newData
    },
    onSuccess: (newData) => {
      queryClient.setQueryData([PAYMENT_DATA_KEY], newData)
    },
  })

  const clearPaymentDataMutation = useMutation<PaymentData | null, Error>({
    mutationFn: async () => {
      return await paymentDataStorage.clearSpecificFields()
    },
    onSuccess: (updatedData) => {
      queryClient.setQueryData([PAYMENT_DATA_KEY], updatedData)
    },
  })

  return {
    paymentData: data,
    setPaymentData: setPaymentDataMutation.mutate,
    clearPaymentData: clearPaymentDataMutation.mutate,
  }
}
